import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";

import { MIN_ADDRESS_LOOKUP_CHARS } from "lib/storefrontAddressLookup";

const GOOGLE_MAPS_SCRIPT_ID = "vbs-google-maps-places-script";
let googleMapsPlacesPromise = null;

function addressKey(value) {
  return String(value?.placeId || value?.formattedAddress || "");
}

function loadGoogleMapsPlaces() {
  const key = String(process.env.REACT_APP_GOOGLE_MAPS_BROWSER_KEY || "").trim();
  if (!key || typeof window === "undefined") return Promise.resolve(null);
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (googleMapsPlacesPromise) return googleMapsPlacesPromise;

  googleMapsPlacesPromise = new Promise((resolve) => {
    const existing = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google || null), { once: true });
      existing.addEventListener("error", () => resolve(null), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places&v=weekly`;
    script.onload = () => resolve(window.google || null);
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });

  return googleMapsPlacesPromise;
}

function mapEmbedSrc(address) {
  // Number(null) === 0, so only treat real coordinate values as a pin.
  const hasCoords =
    address?.latitude != null &&
    address?.longitude != null &&
    address?.latitude !== "" &&
    address?.longitude !== "" &&
    Number.isFinite(Number(address.latitude)) &&
    Number.isFinite(Number(address.longitude));
  if (hasCoords) {
    const lat = Number(address.latitude);
    const lng = Number(address.longitude);
    return `https://www.google.com/maps?q=${encodeURIComponent(`${lat},${lng}`)}&z=17&output=embed`;
  }
  const query = [
    address?.formattedAddress,
    address?.street,
    address?.street1,
    address?.city,
    address?.state,
    address?.postalCode || address?.zip,
    address?.country,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(", ");
  return query ? `https://www.google.com/maps?q=${encodeURIComponent(query)}&z=16&output=embed` : "";
}

function selectedAddressForMap(address, candidates) {
  const selectedKey = addressKey(address);
  if (selectedKey) {
    const matched = (candidates || []).find((row) => addressKey(row) === selectedKey);
    return { ...(matched || {}), ...(address || {}) };
  }
  return address || {};
}

function componentValue(components, type, shortName = false) {
  const match = (Array.isArray(components) ? components : []).find((row) =>
    Array.isArray(row?.types) && row.types.includes(type)
  );
  if (!match) return "";
  return String(shortName ? match.short_name || match.shortText || "" : match.long_name || match.longText || "").trim();
}

function componentValueAny(components, types, shortName = false) {
  for (const type of types) {
    const value = componentValue(components, type, shortName);
    if (value) return value;
  }
  return "";
}

function countryCodeFromComponents(components) {
  const code = componentValue(components, "country", true).toUpperCase();
  if (code) return code;
  const longName = componentValue(components, "country").toLowerCase();
  if (longName.includes("mexico")) return "MX";
  if (longName.includes("united states")) return "US";
  return longName ? longName.toUpperCase() : "";
}

function mapGooglePlaceToCandidate(place) {
  const components = place?.address_components || [];
  const streetNumber = componentValue(components, "street_number");
  const route = componentValue(components, "route");
  const premise = componentValueAny(components, ["premise", "subpremise"]);
  const city = componentValueAny(components, [
    "locality",
    "postal_town",
    "sublocality",
    "administrative_area_level_2",
  ]);
  const district = componentValueAny(components, [
    "neighborhood",
    "sublocality_level_1",
    "sublocality",
  ]);
  const state = componentValueAny(components, ["administrative_area_level_1", "administrative_area_level_2"], true);
  const postalCode = [componentValue(components, "postal_code"), componentValue(components, "postal_code_suffix")]
    .filter(Boolean)
    .join("-");
  const country = countryCodeFromComponents(components);
  const formattedAddress = String(place?.formatted_address || place?.vicinity || "").trim();
  const businessName = String(place?.name || "").trim();
  const lat = place?.geometry?.location?.lat?.();
  const lng = place?.geometry?.location?.lng?.();
  return {
    formattedAddress,
    placeId: String(place?.place_id || "").trim(),
    businessName: businessName && businessName !== formattedAddress ? businessName : "",
    latitude: Number.isFinite(Number(lat)) ? Number(lat) : null,
    longitude: Number.isFinite(Number(lng)) ? Number(lng) : null,
    googleMapsUri: String(place?.url || "").trim(),
    country,
    address: {
      street: [streetNumber, route].filter(Boolean).join(" ").trim() || premise || formattedAddress.split(",")[0] || "",
      district,
      city,
      state,
      postalCode,
      country,
    },
  };
}

function mergeCandidates(primary, fallback) {
  const seen = new Set();
  return [...(primary || []), ...(fallback || [])].filter((row) => {
    const key = addressKey(row);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export default function VerifiedAddressSearch({
  label = "Find address or business",
  placeholder = "Search like Google Maps...",
  rowKey,
  address,
  lookup,
  onTyping,
  onSelect,
  disabled = false,
  required = false,
  showMap = true,
  helperText = "",
}) {
  const [browserCandidates, setBrowserCandidates] = useState([]);
  const [browserLoading, setBrowserLoading] = useState(false);
  const [browserError, setBrowserError] = useState("");
  const requestSeqRef = useRef(0);

  const backendCandidates = lookup?.candidatesByKey?.[rowKey] || [];
  const draft = lookup?.draftsByKey?.[rowKey] ?? String(address?.formattedAddress || "");
  const candidates = useMemo(
    () => mergeCandidates(browserCandidates, backendCandidates),
    [browserCandidates, backendCandidates]
  );
  const mapAddress = useMemo(() => selectedAddressForMap(address, candidates), [address, candidates]);
  const loading = browserLoading || !!lookup?.loadingByKey?.[rowKey];
  const error = lookup?.errorsByKey?.[rowKey] || "";
  const mapSrc = showMap ? mapEmbedSrc(mapAddress) : "";
  const isVerified = !!String(mapAddress?.formattedAddress || "").trim();

  useEffect(() => {
    const query = String(draft || "").trim();
    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;
    setBrowserError("");

    if (disabled || query.length < MIN_ADDRESS_LOOKUP_CHARS) {
      setBrowserCandidates([]);
      setBrowserLoading(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setBrowserLoading(true);
      try {
        const google = await loadGoogleMapsPlaces();
        if (requestSeqRef.current !== requestSeq) return;
        if (!google?.maps?.places?.AutocompleteService || !google?.maps?.places?.PlacesService) {
          setBrowserCandidates([]);
          return;
        }

        const autocomplete = new google.maps.places.AutocompleteService();
        const predictions = await new Promise((resolve) => {
          autocomplete.getPlacePredictions({ input: query }, (results, status) => {
            if (status !== google.maps.places.PlacesServiceStatus.OK || !Array.isArray(results)) {
              resolve([]);
              return;
            }
            resolve(results);
          });
        });
        if (requestSeqRef.current !== requestSeq) return;
        if (!predictions.length) {
          setBrowserCandidates([]);
          return;
        }

        const serviceContainer = document.createElement("div");
        const service = new google.maps.places.PlacesService(serviceContainer);
        const details = await Promise.all(
          predictions.slice(0, 8).map(
            (prediction) =>
              new Promise((resolve) => {
                service.getDetails(
                  {
                    placeId: prediction.place_id,
                    fields: ["place_id", "name", "formatted_address", "address_components", "geometry", "url"],
                  },
                  (place, status) => {
                    if (status !== google.maps.places.PlacesServiceStatus.OK || !place) {
                      resolve(null);
                      return;
                    }
                    resolve(mapGooglePlaceToCandidate(place));
                  }
                );
              })
          )
        );
        if (requestSeqRef.current !== requestSeq) return;
        setBrowserCandidates(details.filter((row) => row?.formattedAddress && row?.placeId));
      } catch {
        if (requestSeqRef.current === requestSeq) {
          setBrowserCandidates([]);
          setBrowserError("Google Maps place search is unavailable. Address lookup fallback is still available.");
        }
      } finally {
        if (requestSeqRef.current === requestSeq) setBrowserLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [disabled, draft]);

  return (
    <Stack spacing={1.25}>
      <Autocomplete
        fullWidth
        value={null}
        options={candidates}
        filterOptions={(options) => options}
        loading={loading}
        inputValue={draft}
        disabled={disabled}
        onInputChange={(_, nextValue, reason) => {
          if (reason !== "input" && reason !== "clear") return;
          onTyping?.(nextValue);
          lookup?.setDraftForRow?.(rowKey, nextValue);
        }}
        onChange={(_, option) => {
          if (!option) return;
          lookup?.applyCandidateForRow?.(rowKey, option);
          onSelect?.(option);
        }}
        getOptionLabel={(option) => String(option?.formattedAddress || "")}
        isOptionEqualToValue={(option, value) => addressKey(option) === addressKey(value)}
        noOptionsText={
          String(draft || "").trim().length >= MIN_ADDRESS_LOOKUP_CHARS
            ? "No matching places found"
            : `Type at least ${MIN_ADDRESS_LOOKUP_CHARS} characters to search addresses and businesses`
        }
        renderOption={(props, option) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <li {...props} key={addressKey(option)}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="button" color="text.primary" sx={{ display: "block", lineHeight: 1.25 }}>
                {option?.businessName || option?.formattedAddress}
              </Typography>
              {option?.businessName && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.25 }}>
                  {option.formattedAddress}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                {[
                  option?.address?.city,
                  option?.address?.state,
                  option?.address?.postalCode,
                  option?.address?.country || option?.country,
                ]
                  .filter(Boolean)
                  .join(" - ")}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...params}
            label={label}
            placeholder={placeholder}
            required={required}
            error={!!error}
            helperText={error || browserError || helperText}
          />
        )}
      />
      {isVerified && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" color="success" variant="outlined" label="Verified Google place" />
          {mapAddress?.businessName && <Chip size="small" variant="outlined" label={mapAddress.businessName} />}
          {mapAddress?.googleMapsUri && (
            <Button size="small" href={mapAddress.googleMapsUri} target="_blank" rel="noreferrer">
              Open in Google Maps
            </Button>
          )}
        </Stack>
      )}
      {mapSrc && (
        <Box
          sx={{
            width: "100%",
            height: 220,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "background.default",
          }}
        >
          <iframe
            title={`${label} map`}
            src={mapSrc}
            width="100%"
            height="100%"
            style={{ border: 0, display: "block" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </Box>
      )}
    </Stack>
  );
}

VerifiedAddressSearch.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  rowKey: PropTypes.string.isRequired,
  address: PropTypes.shape({
    formattedAddress: PropTypes.string,
    businessName: PropTypes.string,
    latitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    longitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    googleMapsUri: PropTypes.string,
  }),
  lookup: PropTypes.shape({
    candidatesByKey: PropTypes.objectOf(PropTypes.array),
    draftsByKey: PropTypes.objectOf(PropTypes.string),
    loadingByKey: PropTypes.objectOf(PropTypes.bool),
    errorsByKey: PropTypes.objectOf(PropTypes.string),
    setDraftForRow: PropTypes.func,
    applyCandidateForRow: PropTypes.func,
  }).isRequired,
  onTyping: PropTypes.func,
  onSelect: PropTypes.func,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  showMap: PropTypes.bool,
  helperText: PropTypes.string,
};

VerifiedAddressSearch.defaultProps = {
  label: "Find address or business",
  placeholder: "Search like Google Maps...",
  address: {},
  onTyping: undefined,
  onSelect: undefined,
  disabled: false,
  required: false,
  showMap: true,
  helperText: "",
};
